import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  format,
  isValid,
  parseISO,
} from 'date-fns';
import { arrayHasSameMembers } from 'lib/arrayHasSameMembers/arrayHasSameMembers';
import { intersection } from 'lodash';
import { AssessmentQuestionCriterion } from 'store/assessments/types';
import { FieldResult, TimescalePeriod } from 'types/types';

export const testCriterion = (
  criterion: AssessmentQuestionCriterion,
  {
    fieldResults,
    variables,
  }: {
    fieldResults: Record<string, FieldResult>;
    variables: Record<string, string>;
  },
): boolean => {
  // Get value directly from criterion.fieldValue OR from criterion.fieldId
  let { fieldValue }: { fieldValue?: string | string[] | undefined } =
    criterion;
  if (fieldValue === undefined && criterion.fieldId) {
    fieldValue = fieldResults[criterion.fieldId]?.value;
  }
  if (fieldValue === undefined) {
    if (
      criterion.allowBlank ||
      (criterion.operator === '=' &&
        !criterion.varValue &&
        criterion.value === '')
    ) {
      return true;
    }
    return false;
  }
  // Get value to test against
  let testValue: string | string[] = criterion.value || '';
  if (criterion.varValue) {
    if (!variables[criterion.varValue]) {
      return !!criterion.allowVarValueBlank;
    }
    testValue = variables[criterion.varValue]!;
    // Modify date of testValue
    if (
      testValue &&
      criterion.dateModifiers?.length &&
      isValid(parseISO(testValue))
    ) {
      testValue = format(
        criterion.dateModifiers.reduce(
          (date, modifier) =>
            modifier.period === TimescalePeriod.day
              ? addDays(date, modifier.unit)
              : modifier.period === TimescalePeriod.week
                ? addWeeks(date, modifier.unit)
                : modifier.period === TimescalePeriod.month
                  ? addMonths(date, modifier.unit)
                  : addYears(date, modifier.unit),
          parseISO(testValue),
        ),
        'yyyy-MM-dd',
      );
    }
    // Modify date format of testValue
    if (
      testValue &&
      criterion.varValueDateFormat &&
      isValid(parseISO(testValue))
    ) {
      testValue = format(parseISO(testValue), criterion.varValueDateFormat);
    }
  }
  if (criterion.operator === 'includes' || criterion.operator === '!includes') {
    const result = !!intersection(
      Array.isArray(testValue) ? testValue : testValue ? [testValue] : [],
      Array.isArray(fieldValue) ? fieldValue : fieldValue ? [fieldValue] : [],
    ).length;
    return criterion.operator === 'includes' ? result : !result;
  }
  if (criterion.operator === '=' || criterion.operator === '!=') {
    if (Array.isArray(testValue)) {
      if (!Array.isArray(fieldValue)) {
        return false;
      }
      return criterion.operator === '='
        ? arrayHasSameMembers(testValue, fieldValue)
        : !arrayHasSameMembers(testValue, fieldValue);
    }
    if (Array.isArray(testValue)) {
      return false;
    }
    // eslint-disable-next-line eqeqeq
    return testValue == fieldValue;
  }
  return Array.isArray(testValue) !== Array.isArray(fieldValue)
    ? false
    : criterion.operator === '>'
      ? fieldValue > testValue
      : criterion.operator === '>='
        ? fieldValue >= testValue
        : criterion.operator === '<'
          ? fieldValue < testValue
          : fieldValue <= testValue;
  // : criterion.operator === '<='
  // ? fieldValue <= testValue
  // : false;
};
